import Icon from "@common/controls/Icon/component";
import InfiniteScroll from "@common/controls/InfiniteScroll/component";
import TextInput from "@common/controls/Inputs/Text/component";
import Preloader from "@common/controls/Preloader/component";
import withDropdownHOC, {
  type WithDropdownCmpProps,
  type WithDropdownComponentAddedProps,
  type WithDropdownToggleProps
} from "@common/hocs/withDropdown";
import { ContentAlign } from "@common/hocs/withDropdown/lib";
import withDynamicOptionsHOC, { type WithDynamicOptionsAddedProps } from "@common/hocs/withDynamicOptions";
import { type FetchOptionsArgs } from "@common/hocs/withLazy";
import withValue from "@common/hocs/withValue";
import { useI18n } from "@common/hooks/i18n";
import { sort } from "@common/lib/array";
import { usePromise, useUpdated } from "@common/lib/hooks";
import { Common } from "@common/types";
import classnames from "classnames";
import { groupBy } from "lodash";
import * as React from "react";
import { HeadingSelectItem, type SelectHeading } from "../Base/component";
import { SelectOptionDescription } from "../SelectOptionDescription/component";
import { type OptionItem, type WithOptions } from "../component";
import withAddHOC, { type WithAddHOCBaseProps } from "../hocs/withAdd";
import { getAvailableOptions, getOptionLabel } from "./lib";

interface AutoCompleteToggleProps {
  className?: string;
  containerClassName?: string;
  icon?: Common.IconType;
  inputValue: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  name?: string;
  placeholder?: string;
  qa?: string;
  style?: React.CSSProperties;
  toggleOnClick?: boolean;

  onChangeDelay?: number;
  onClose?: () => void;
  onInputChange: (value: string) => void;
}

const AutoCompleteToggle = (props: WithDropdownToggleProps & AutoCompleteToggleProps) => {
  const i18n = useI18n();

  useUpdated(() => {
    if (props.onClose && !props.isOpen) {
      props.onClose();
    }
  }, [props.isOpen, props.isDisabled]);

  const onClick = React.useCallback(() => {
    props.toggleOnClick && props.toggleOpen();
  }, [props.toggleOnClick, props.toggleOpen]);

  const onInputChange = React.useCallback(
    (value: string) => {
      props.onInputChange(value);
      !props.isOpen && props.open();
    },
    [props.onInputChange, props.isOpen, props.open]
  );

  return (
    <div className={classnames("auto_complete", props.containerClassName)}>
      {props.icon && <Icon icon={props.icon} />}

      <TextInput
        autoComplete="off"
        addon={props.isLoading ? <Preloader isFontSize /> : undefined}
        className={props.className}
        name={props.name}
        ref={props.toggleRef}
        isDisabled={props.isDisabled}
        placeholder={props.placeholder || i18n.typeToSearch}
        onChange={onInputChange}
        onChangeTrigger={props.onChangeDelay}
        onClick={onClick}
        qa={props.qa}
        style={props.style}
        value={props.inputValue}
      />
    </div>
  );
};

export type AutoCompleteContentProps = {
  dropdownClassName?: string;
  heading?: SelectHeading | SelectHeading[];
  infiniteLoaderKey?: string;
  noOptionsLabel?: string;
  options?: OptionItem<any>[];
  hasMore?: boolean;
  loadMore?: () => Promise<void>;

  onItemSelected: (value: OptionItem<any>) => void;
  renderItemAddon?: (item: OptionItem<any>) => any;
};

type State = {
  isLoading: boolean;
};
const AutoCompleteContent = (props: WithDropdownCmpProps & AutoCompleteContentProps) => {
  const i18n = useI18n();

  const [state, setState] = React.useState<State>({ isLoading: false });

  const headings = React.useMemo(
    () => (props.heading ? (Array.isArray(props.heading) ? props.heading : [props.heading]) : null),
    [props.heading]
  );
  const groupped = React.useMemo(() => groupBy(props.options, (x) => x.group ?? ""), [props.options]);
  const groupKeys = React.useMemo(() => Object.keys(groupped), [groupped]);

  const onHeadingClick = (heading: SelectHeading) => {
    const { isToggle, onClick } = heading;
    isToggle && props.close();
    onClick && setTimeout(onClick, 0);
  };

  const loadMore = async (): Promise<void> => {
    if (!props.loadMore) return Promise.resolve();

    try {
      setState({ isLoading: true });

      await props.loadMore();
    } finally {
      setState({ isLoading: false });
    }
  };

  const renderItems = (options: OptionItem<any>[]) =>
    options.map((opt) => (
      <li key={opt.value} data-name={opt.value} className="item" value={opt.value}>
        {(opt.label || opt.descriptions?.length) && (
          <span
            className="item-content"
            onClick={() => {
              props.onItemSelected(opt);
              props.close();
            }}
          >
            {opt.label && (
              <div className="info">
                <span className="content">
                  {opt.prepend}
                  {opt.label}
                </span>
                {props.renderItemAddon && props.renderItemAddon(opt)}
              </div>
            )}
            {opt.descriptions ? <SelectOptionDescription descriptions={opt.descriptions} /> : null}
          </span>
        )}
      </li>
    ));

  return (
    <>
      {!!headings &&
        headings.map((x, i) => (
          <HeadingSelectItem key={i} hint={x.hint} isClickable={!!x.onClick} onClick={() => onHeadingClick(x)}>
            {x.content}
          </HeadingSelectItem>
        ))}

      <InfiniteScroll
        key={props.infiniteLoaderKey ?? "loader"}
        className={classnames("select_options", props.dropdownClassName)}
        element="ul"
        hasMore={props.hasMore}
        loader={
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: 8
            }}
          >
            <Preloader relative isFontSize />
          </div>
        }
        loadMore={loadMore}
        height={200}
      >
        {props.options?.length ? (
          groupKeys.length === 1 ? (
            renderItems(groupped[groupKeys[0]])
          ) : (
            groupKeys.map((group) => (
              <>
                {group && (
                  <li key={group} className="group">
                    <div className="group_content hint">{group}</div>
                  </li>
                )}
                {renderItems(groupped[group])}
              </>
            ))
          )
        ) : state.isLoading ? null : (
          <li className="item disabled no_match">
            <span className="item-content">{props.noOptionsLabel || i18n.noMatches}</span>
          </li>
        )}
      </InfiniteScroll>
    </>
  );
};

const AutoCompleteDropdown = withDropdownHOC<AutoCompleteToggleProps, AutoCompleteContentProps>(
  AutoCompleteToggle,
  AutoCompleteContent,
  {
    disableKeyboardToggle: true,
    dontFocusContentOnOpen: true
  }
);

type AutoCompleteBaseProps = WithOptions<any> &
  Common.InputTypeProps<any> & {
    containerClassName?: string;
    children?: any;
    className?: string;
    dropdownClassName?: string;
    heading?: SelectHeading;
    icon?: Common.IconType;
    isDisabled?: boolean;
    isLoading?: boolean;
    isRequired?: boolean;
    name?: string;
    noMatchLabel?: string;
    onSearchTextChange?: (value: string) => void;
    placeholder?: string;
    searchText: string;
    sort?: Common.SortOrder;
    style?: React.CSSProperties;
    toggleOnClick?: boolean;
    inputValue?: string;
    outputType?: "value" | "option";
    shouldResetOnChange?: boolean;
    renderItemAddon?: (item: OptionItem<any>) => any;
    transformSearchText?: (value: string) => string;
  };

export type AutoCompleteProps = Omit<AutoCompleteBaseProps, "infiniteLoaderKey" | "searchText"> &
  Omit<WithDropdownComponentAddedProps, "align"> & {
    excludeOptionValues?: any[];
  };

const AutoCompleteBase = (props: AutoCompleteProps) => {
  const [searchText, setSearchText] = React.useState(getOptionLabel(props.options, props.value));

  React.useEffect(
    () => setSearchText(getOptionLabel(props.options, props.value) || (props.inputValue ?? "")),
    [props.options, props.value, props.inputValue]
  );

  const options = React.useMemo(
    () =>
      sort(
        getAvailableOptions(props.options, props.excludeOptionValues).filter(
          (opt) =>
            (opt.label || "").toLowerCase().includes(searchText.toLowerCase()) ||
            opt.descriptions?.some((x) => x.label.toLowerCase().includes(searchText.toLowerCase()))
        ),
        props.sort ? { key: "label", order: props.sort, type: "natural" } : undefined
      ),
    [props.excludeOptionValues, props.options, props.sort, searchText]
  );

  const onItemSelected = React.useCallback(
    (option: OptionItem<any>) => {
      props.onChange(props.outputType === "option" ? option : option.value);

      setSearchText(props.shouldResetOnChange ? "" : getOptionLabel(options, option.value));
    },
    [options, props.onChange, setSearchText, props.shouldResetOnChange]
  );

  const onSearchTextChange = React.useCallback(
    (value: string) => {
      if (!value) props.onChange(null);

      let finalValue = value;
      props.transformSearchText && (finalValue = props.transformSearchText(value));
      props.onSearchTextChange && props.onSearchTextChange(finalValue);

      setSearchText(finalValue);
    },
    [props.onChange, setSearchText]
  );

  return (
    <AutoCompleteDropdown
      align={ContentAlign.Stretch}
      className={props.className}
      containerClassName={props.containerClassName}
      dropdownClassName={classnames("auto_complete_dropdown_container", props.dropdownClassName)}
      heading={props.heading}
      icon={props.icon}
      inputValue={searchText}
      isDisabled={props.isDisabled}
      isLoading={props.isLoading}
      maxHeight={props.maxHeight}
      name={props.name}
      noOptionsLabel={props.noMatchLabel}
      onInputChange={onSearchTextChange}
      onItemSelected={onItemSelected}
      options={options}
      placeholder={props.placeholder}
      renderItemAddon={props.renderItemAddon}
      style={props.style}
      toggleOnClick={props.toggleOnClick}
    />
  );
};

const AutoComplete = withValue<any, AutoCompleteProps>(AutoCompleteBase);
export default AutoComplete;

const AutoCompleteFreeBase = (props: Omit<AutoCompleteProps, "inputValue" | "onSearchTextChange">) => {
  return <AutoCompleteBase {...props} inputValue={props.value} onSearchTextChange={props.onChange} />;
};
export const AutoCompleteFree = withValue<any, AutoCompleteProps>(AutoCompleteFreeBase);

export const AutoCompleteWithAdd = withAddHOC(AutoComplete);

export const DynamicAutoComplete = withDynamicOptionsHOC<any, AutoCompleteProps>(AutoComplete);

const DynamicAutoCompleteWithAddBase = withAddHOC<
  Omit<AutoCompleteProps, "options"> & WithDynamicOptionsAddedProps<any>
>(DynamicAutoComplete);
export const DynamicAutoCompleteWithAdd = ({
  dropdownClassName,
  ...props
}: Omit<AutoCompleteProps, "options"> & WithDynamicOptionsAddedProps<any> & WithAddHOCBaseProps) => (
  <DynamicAutoCompleteWithAddBase {...props} dropdownClassName={classnames(dropdownClassName, "with_add")} />
);

export type GetOptions = (args: FetchOptionsArgs) => Promise<{ items: OptionItem<any>[]; hasMore: boolean }>;
export type AutoCompleteLazyProps = Omit<
  AutoCompleteBaseProps,
  "hasMore" | "infiniteLoaderKey" | "loadMore" | "onSearchTextChange" | "options" | "searchText"
> &
  Omit<WithDropdownComponentAddedProps, "align"> & {
    defaultSearchText?: string;
    dependent?: any;
    fetchDefaultOption?: (value: any) => Promise<OptionItem<any>>;
    fetchOptions: GetOptions;
    outputType?: "value" | "option";
  };
export const AutoCompleteLazy = ({
  className,
  defaultSearchText,
  fetchDefaultOption,
  fetchOptions,
  heading,
  onChange,
  outputType,
  ...props
}: AutoCompleteLazyProps) => {
  const [hasMore, setHasMore] = React.useState(true);
  const [page, setPage] = React.useState(0);
  const [hasSearchTextChanged, setHasSearchTextChanged] = React.useState<boolean>(false);
  const [searchText, setSearchText] = React.useState<string>(defaultSearchText ?? "");
  const [shouldSearch, setShouldSearch] = React.useState<boolean>(true);
  const [options, setOptions] = React.useState<OptionItem<any>[]>([]);

  const [defaultOption] = usePromise({
    fn: fetchDefaultOption!,
    args: [props.value],
    shouldLoad: !!fetchDefaultOption && !!props.value && !searchText
  });

  useUpdated(() => setSearchText(defaultOption?.label ?? defaultSearchText ?? ""), [defaultSearchText, defaultOption]);

  useUpdated(() => {
    setPage(1);
    setHasMore(true);
    setShouldSearch(true);
  }, [props.dependent]);

  const loadMore = React.useCallback(async () => {
    if (!shouldSearch) return;

    const optionsPage = await fetchOptions({
      search: hasSearchTextChanged ? searchText : "",
      page: page + 1,
      size: 20
    });

    const hasMore = optionsPage.hasMore;

    setPage(page + 1);
    setHasMore(hasMore);
    setOptions([...options, ...optionsPage.items]);
  }, [fetchOptions, hasSearchTextChanged, options, page, setPage, searchText, setHasMore, setOptions, shouldSearch]);

  const onItemSelected = React.useCallback(
    (option: OptionItem<any>) => {
      setShouldSearch(false);
      setHasSearchTextChanged(false);
      setSearchText(getOptionLabel(options, option.value));
      onChange(outputType === "option" ? option : option.value);
    },
    [setSearchText, onChange, options, outputType, setHasSearchTextChanged, setShouldSearch]
  );

  const onSearchTextChange = React.useCallback(
    (value: string) => {
      if (!value) onChange(null);

      setHasSearchTextChanged(true);
      setHasMore(true);
      setOptions([]);
      setPage(0);
      setSearchText(value);
      setShouldSearch(true);
    },
    [onChange, setHasMore, setHasSearchTextChanged, setOptions, setPage, setSearchText, setShouldSearch]
  );

  return (
    <AutoCompleteDropdown
      {...props}
      align={ContentAlign.Stretch}
      className={className}
      dropdownClassName={classnames("auto_complete_dropdown_container", props.dropdownClassName)}
      hasMore={hasMore}
      heading={heading}
      loadMore={loadMore}
      infiniteLoaderKey={searchText}
      inputValue={searchText}
      maxHeight={props.maxHeight}
      options={options}
      onChangeDelay={300}
      onInputChange={onSearchTextChange}
      onItemSelected={onItemSelected}
      toggleOnClick={props.toggleOnClick}
    />
  );
};

const AutoCompleteLazyWithAddBase = withAddHOC<AutoCompleteLazyProps>(AutoCompleteLazy);
export const AutoCompleteLazyWithAdd = ({
  dropdownClassName,
  ...props
}: AutoCompleteLazyProps & WithAddHOCBaseProps) => (
  <AutoCompleteLazyWithAddBase {...props} dropdownClassName={classnames(dropdownClassName, "with_add")} />
);
